import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package, AlertTriangle, ShoppingCart, Truck, CalendarClock, Clock4, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KPICard } from "@/components/KPICard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/constants";
import { formatDateTime } from "@/lib/format";
import { WebshopOrdersStrip } from "@/components/WebshopOrdersStrip";
import { SYNC_EVENT } from "@/lib/useWebshopStatus";

const REFRESH_MS = 5 * 60 * 1000;

export default function Dashboard() {
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/kpis");
      setKpis(data);
      setLastRefresh(new Date());
    } finally { setLoading(false); }
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/woocommerce/sync");
      if (data.state === "error") toast.error("Bijwerken mislukt", { description: data.error_message });
      else toast.success("Gegevens bijgewerkt vanuit kipkruiden.nl");
    } catch {
      toast.error("Webshop-koppeling is nog niet ingesteld");
    } finally {
      setSyncing(false);
      window.dispatchEvent(new Event(SYNC_EVENT));
      load();
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const cards = kpis ? [
    { id: "op-voorraad", label: "Producten op voorraad", value: kpis.op_voorraad,
      sublabel: "Voldoende beschikbaar", tone: "healthy", icon: Package, link: "/voorraad?stock_status=op_voorraad" },
    { id: "lage-voorraad", label: "Producten met lage voorraad", value: kpis.lage_voorraad,
      sublabel: "Aandacht nodig", tone: "low", icon: AlertTriangle, link: "/voorraad?stock_status=laag" },
    { id: "te-bestellen", label: "Producten die besteld moeten worden", value: kpis.te_bestellen,
      sublabel: "Direct actie", tone: "critical", icon: ShoppingCart, link: "/inkoopadvies" },
    { id: "in-bestelling", label: "Producten in bestelling", value: kpis.in_bestelling,
      sublabel: "Loopt bij leverancier", tone: "in-transit", icon: Truck, link: "/bestellingen" },
    { id: "leveringen-7d", label: "Leveringen komende 7 dagen", value: kpis.leveringen_7d,
      sublabel: "Verwacht binnen 1 week", tone: "in-transit", icon: CalendarClock, link: "/bestellingen?quick=deze_week" },
    { id: "vertraagd", label: "Vertraagde leveringen", value: kpis.vertraagd,
      sublabel: "Actie vereist", tone: "critical", icon: Clock4, link: "/bestellingen?quick=vertraagd" },
  ] : [];

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overzicht"
        title={`Welkom bij ${COMPANY.name}`}
        description="In één oogopslag: wat is bijna op, wat moet worden besteld, en wat komt eraan."
        action={
          <Button data-testid="refresh-dashboard" variant="outline" className="touch-tap" onClick={syncNow} disabled={syncing}>
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> Nu bijwerken
          </Button>
        }
      />

      {loading && !kpis ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, i) => <div key={`kpi-sk-${i}`} className="surface h-56 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="kpi-grid">
          {cards.map((c) => (
            <button
              key={c.id}
              onClick={() => nav(c.link)}
              data-testid={`kpi-${c.id}`}
              className="text-left focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-xl"
            >
              <KPICard label={c.label} value={c.value} sublabel={c.sublabel} tone={c.tone} icon={c.icon} />
            </button>
          ))}
        </div>
      )}

      {kpis?.webshop && <WebshopOrdersStrip webshop={kpis.webshop} />}

      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 text-sm text-muted-foreground" data-testid="dashboard-footer">
        <span className="font-mono uppercase tracking-widest text-xs">
          Laatste webshop-update: {kpis?.webshop?.last_success ? formatDateTime(kpis.webshop.last_success) : "—"}
        </span>
        <span className="text-xs" data-testid="dashboard-refresh-note">
          Gegevens uit kipkruiden.nl worden elke 10 minuten automatisch bijgewerkt (scherm ververst: {lastRefresh ? formatDateTime(lastRefresh.toISOString()) : "—"}). Klik op een kaart om direct het overzicht te openen.
        </span>
      </div>
    </div>
  );
}
